import Joi from "joi-browser";
import React, { useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";

import Lottie from "lottie-react";
import * as contactLoading from "../animations/profile.json";
import contactOptionsets from "../options/contact.json";
import {
  formatDate,
  getFieldDate,
  getFieldFormattedValue,
  getFieldName,
  getFieldText,
  updateEntity,
} from "../scripts/helperScripts";
import AddressesEntity from "./AddressesEntity";
import BackIcon from "./common/BackIcon";
import Checkbox from "./common/Checkbox";
import Input from "./common/Input";
import PhoneInput from "./common/PhoneInput";
import Select from "./common/Select";
import {
  getAddresses,
  getContact,
  getReferringAgencies,
} from "./services/soar";
import FormCheckbox from "./common/FormCheckbox";

const MENATooltip = (
  <>
    <div className="h4 text-white">
      Middle Eastern or North African (MENA) Countries
    </div>
    <table className="table table-bordered fs-6">
      <tr>
        <td colSpan="3">
          The following countries fall under the category of Middle Eastern or
          North African (MENA):
        </td>
      </tr>
      <tr>
        <td>1. Afghanistan</td>
        <td>9. Kuwait</td>
        <td>17. Somalia</td>
      </tr>
      <tr>
        <td>2. Algeria</td>
        <td>10. Lebanon</td>
        <td>18. Sudan</td>
      </tr>
      <tr>
        <td>3. Bahrain</td>
        <td>11. Libya</td>
        <td>19. Syria</td>
      </tr>
      <tr>
        <td>4. Djibouti</td>
        <td>12. Morocco</td>
        <td>20. Sudan</td>
      </tr>
      <tr>
        <td>5. Egypt</td>
        <td>13. Oman</td>
        <td>21. Syria</td>
      </tr>
      <tr>
        <td>6. Iran</td>
        <td>14. Pakistan</td>
        <td>22. Tunisia</td>
      </tr>
      <tr>
        <td>7. Iraq</td>
        <td>15. Palestine</td>
        <td>23. Türkiye</td>
      </tr>
      <tr>
        <td>8. Israel</td>
        <td>16. Qatar</td>
        <td></td>
      </tr>
    </table>
  </>
);

const Contact = ({ propContactID, postSave, forceSave }) => {
  let { contactID } = useParams();
  const [clientId, setClientId] = useState(contactID);
  const [loaded, setLoaded] = useState(false);
  const [clientData, setClientData] = useState({});
  const [addresses, setAddresses] = useState([]);
  const [updateAddresses, setUpdateAddresses] = useState(false);
  const [countyOptions, setCountyOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const isMountedRef = useRef(true);
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  //Validation Schema
  const joiSchema = {
    contactid: Joi.string(),
    ccx_contacttype: Joi.string().required().label("Contact Type"),
    ccx_mpistr: Joi.string().allow(""),
    firstname: Joi.string().allow(""),
    middlename: Joi.string().allow(""),
    lastname: Joi.string().required().label("Last Name"),
    ccx_namesuffix: Joi.string().allow(""),
    ccx_alias1: Joi.string().allow(""),
    ccx_alias2: Joi.string().allow(""),
    gendercode: Joi.string().allow(""),
    sor_preferredgender: Joi.string().allow(""),
    sor_pronouns: Joi.string().allow(""),
    sor_pronounsother: Joi.string().allow(""),
    birthdate: Joi.date()
      .max("now")
      .min(new Date("1/1/1900"))
      .label("DOB")
      .allow("")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `DOB cannot be future dated`;
              break;
            case "date.min":
              error.message = `DOB cannot be before 1/1/1900`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    ccx_ssn: Joi.string()
      .label("SSN")
      .max(11)
      .regex(
        /^(?!(000|666|9))\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/,
        "SSN (xxx-xx-xxxx)"
      )
      .allow(""),
    sor_consideryourselfhispaniclatino: Joi.boolean(),
    sor_americanindianoralaskanative: Joi.boolean(),
    sor_nativehawaiianorotherpacificislander: Joi.boolean(),
    sor_blackorafricanamerican: Joi.boolean(),
    sor_white: Joi.boolean(),
    sor_asian: Joi.boolean(),
    sor_mena: Joi.boolean(),
    sor_primarylanguage: Joi.string().allow(""),
    sor_sexualorientation: Joi.string().allow(""),
    sor_pregnantorparentingteen: Joi.boolean(),
    sor_suiciderisk: Joi.string().allow(""),
    sor_safetyplanstatus: Joi.string().allow(""),
    telephone2: Joi.string().allow(""),
    mobilephone: Joi.string().allow(""),
    telephone1: Joi.string().allow(""),
    fax: Joi.string().allow(""),
    emailaddress1: Joi.string().allow(""),
    emailaddress2: Joi.string().allow(""),
    ccx_primarycontactmethod: Joi.string().allow(""),
    ccx_secondarycontactmethod: Joi.string().allow(""),
    sor_text: Joi.string().allow(""),
    parentcustomerid: Joi.string().allow(""),
    sfy_referringagency: Joi.string().allow(""),
    sor_clientflags: Joi.any(),
  };

  //Route Access Protection
  useEffect(() => {
    if (safyInfo) {
      const modules = safyInfo.user.modules;
      if (
        modules.includes("My Caseload") === false &&
        modules.includes("Team Caseload") === false
      ) {
        navigate("/");
      }
    } else navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  useEffect(() => {
    console.log("forceSaveUseEffect", forceSave);
    if (forceSave) {
      handleSave();
    }
  }, [forceSave]);

  useEffect(() => {
    if (clientId === undefined && propContactID) setClientId(propContactID);
  }, [clientId, propContactID]);

  useEffect(() => {
    console.log("Getting Contact Info");
    if (clientId !== null && clientId !== undefined) {
      getContact(clientId).then((contact) => {
        if (isMountedRef.current) {
          if (contact) {
            const clientObj = {
              contactid: contact[0]["contactid"]
                ? contact[0]["contactid"]
                : null,
              ccx_contacttype: getFieldFormattedValue(
                contact[0]["ccx_contacttype"]
              ),
              ccx_mpistr: contact[0]["ccx_mpistr"],
              firstname: contact[0]["firstname"],
              middlename: contact[0]["middlename"],
              lastname: contact[0]["lastname"],
              ccx_namesuffix: getFieldFormattedValue(
                contact[0]["ccx_namesuffix"]
              ),
              ccx_alias1: contact[0]["ccx_alias1"],
              ccx_alias2: contact[0]["ccx_alias2"],
              gendercode: getFieldFormattedValue(contact[0]["gendercode"]),
              sor_preferredgender: getFieldFormattedValue(
                contact[0]["sor_preferredgender"]
              ),
              sor_pronouns: getFieldText(contact[0]["sor_pronouns"]),
              sor_pronounsother: contact[0]["sor_pronounsother"],
              birthdate: getFieldDate(contact[0]["birthdate"]),
              ccx_ssn: contact[0]["ccx_ssn"],
              telephone2: contact[0]["telephone2"],
              mobilephone: contact[0]["mobilephone"],
              telephone1: contact[0]["telephone1"],
              fax: contact[0]["fax"],
              emailaddress1: contact[0]["emailaddress1"],
              emailaddress2: contact[0]["emailaddress2"],
              ccx_primarycontactmethod: getFieldText(
                contact[0]["ccx_primarycontactmethod"]
              ),
              ccx_secondarycontactmethod: getFieldText(
                contact[0]["ccx_secondarycontactmethod"]
              ),
              sor_text: getFieldText(contact[0]["sor_text"]),
              sor_consideryourselfhispaniclatino:
                getFieldName(
                  contact[0]["sor_consideryourselfhispaniclatino"]
                ) === "Yes"
                  ? true
                  : false,
              sor_americanindianoralaskanative:
                getFieldName(contact[0]["sor_americanindianoralaskanative"]) ===
                "Yes"
                  ? true
                  : false,
              sor_nativehawaiianorotherpacificislander:
                getFieldName(
                  contact[0]["sor_nativehawaiianorotherpacificislander"]
                ) === "Yes"
                  ? true
                  : false,
              sor_blackorafricanamerican:
                getFieldName(contact[0]["sor_blackorafricanamerican"]) === "Yes"
                  ? true
                  : false,
              sor_white:
                getFieldName(contact[0]["sor_white"]) === "Yes" ? true : false,
              sor_asian:
                getFieldName(contact[0]["sor_asian"]) === "Yes" ? true : false,
              sor_mena:
                getFieldName(contact[0]["sor_mena"]) === "Yes" ? true : false,
              sor_primarylanguage: getFieldFormattedValue(
                contact[0]["sor_primarylanguage"]
              ),
              sor_sexualorientation: getFieldFormattedValue(
                contact[0]["sor_sexualorientation"]
              ),
              sor_pregnantorparentingteen:
                getFieldName(contact[0]["sor_pregnantorparentingteen"]) ===
                "Yes"
                  ? true
                  : false,
              sor_suiciderisk: getFieldFormattedValue(
                contact[0]["sor_suiciderisk"]
              ),
              sor_safetyplanstatus: getFieldFormattedValue(
                contact[0]["sor_safetyplanstatus"]
              ),
              sor_clientflags: contact[0].sor_clientflags?.split(",") || [],
              parentcustomerid: getFieldText(contact[0].parentcustomerid),
              sfy_referringagency: contact[0].sfy_referringagency,
            };
            setClientData(clientObj);

            getReferringAgencies(
              getFieldName(contact[0].owningbusinessunit)
            ).then((response) => {
              if (isMountedRef.current) {
                if (response) {
                  const options = response.map((agency) => {
                    return { _id: agency.accountid, name: agency.name };
                  });

                  setCountyOptions(options);
                }
                setLoaded(true);
              }
            });
          }
        }
      });
    }

    // #region Contact Metadata
    // console.log("Getting Contact Metadata");
    // getContactMetadata().then((response) => {
    //   const attributes = JSON.parse(response).Attributes;
    //   console.log("Contact Metadata: ", attributes);
    //   if (response && isMountedRef.current) {
    //     const options = {};
    //     options["ccx_contacttype"] = filterOptionSetAttribute(
    //       attributes,
    //       "ccx_contacttype"
    //     );
    //     options["gendercode"] = filterOptionSetAttribute(
    //       attributes,
    //       "gendercode"
    //     );
    //     options["sor_preferredgender"] = filterOptionSetAttribute(
    //       attributes,
    //       "sor_preferredgender"
    //     );
    //     options["sor_sexualorientation"] = filterOptionSetAttribute(
    //       attributes,
    //       "sor_sexualorientation"
    //     );
    //     options["sor_suiciderisk"] = filterOptionSetAttribute(
    //       attributes,
    //       "sor_suiciderisk"
    //     );
    //     options["sor_safetyplanstatus"] = filterOptionSetAttribute(
    //       attributes,
    //       "sor_safetyplanstatus"
    //     );
    //     options["sor_primarylanguage"] = filterOptionSetAttribute(
    //       attributes,
    //       "sor_primarylanguage"
    //     );
    //     options["ccx_primarycontactmethod"] = filterOptionSetAttribute(
    //       attributes,
    //       "ccx_primarycontactmethod"
    //     );
    //     options["ccx_secondarycontactmethod"] = filterOptionSetAttribute(
    //       attributes,
    //       "ccx_secondarycontactmethod"
    //     );

    //     setContactOptions(options);
    //     setLoaded(true);
    //     // setGenderOptions(filterOptionSetAttribute(attributes, "gendercode"));
    //     // setPreferredGenderOptions(
    //     //   filterOptionSetAttribute(attributes, "sor_preferredgender")
    //     // );
    //     // setPresentOnAdmissionOptions(
    //     //   filterOptionSetAttribute(attributes, "ccx_presentonadmissionos")
    //     // );
    //     // setExpirationReasonOptions(
    //     //   filterOptionSetAttribute(attributes, "ccx_expirationreason")
    //     // );
    //   }
    //});
    // #endregion
  }, [clientId]);

  useEffect(() => {
    if (!clientId) {
      return;
    }

    const loadAddresses = async () => {
      const response = await getAddresses(clientId);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAddresses(response);
    };

    loadAddresses();
    setUpdateAddresses(false);
  }, [clientId, updateAddresses]);

  const handleUpdateAddresses = () => {
    setUpdateAddresses(true);
  };

  const handleChecked = ({ currentTarget: input }) => {
    console.log("value: ", input.checked);
    input.value = input.checked;
    const errs = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) {
      errs[input.name] = errorMessage;
      console.log("Errors:", JSON.stringify(errs));
    } else {
      delete errs[input.name];
      const data = clientData;
      data[input.name] = input.checked;
      setClientData(data);
    }

    setErrors(errs);
  };

  const handleChange = ({ currentTarget: input }) => {
    console.log(`input: ${input.name}`);
    console.log(`value: ${input.value}`);
    setClientData((cd) => {
      const obj = { ...cd };
      obj[input.name] = input.value;
      return obj;
    });

    setErrors((e) => {
      const errs = { ...e };
      const errorMessage = validateProperty(input);
      if (errorMessage) {
        errs[input.name] = errorMessage;
        console.log("Errors:", JSON.stringify(errs));
      } else {
        delete errs[input.name];
      }
      return errs;
    });
  };

  const handleClientFlagsChange = ({ currentTarget: input }) => {
    setClientData((cd) => {
      const obj = { ...cd };
      if (obj.sor_clientflags.includes(input.name)) {
        obj.sor_clientflags = obj.sor_clientflags.filter(
          (e) => e !== input.name
        );
      } else {
        obj.sor_clientflags.push(input.name);
      }
      return obj;
    });
  };

  const cancel = () => {
    navigate(-1);
  };

  const handleSave = () => {
    const obj = {
      ccx_contacttype: {
        type: "optionset",
        value: clientData.ccx_contacttype ? clientData.ccx_contacttype : null,
      },
      sfy_referringagency: {
        type: "entity",
        value: {
          entityName: "account",
          entityId:
            clientData.sfy_referringagency !== undefined
              ? clientData.sfy_referringagency
              : null,
        },
      },
      firstname: {
        type: "string",
        value: clientData.firstname !== undefined ? clientData.firstname : null,
      },
      middlename: {
        type: "string",
        value:
          clientData.middlename !== undefined ? clientData.middlename : null,
      },
      lastname: {
        type: "string",
        value: clientData.lastname !== undefined ? clientData.lastname : null,
      },
      ccx_namesuffix: {
        type: "optionset",
        value: clientData.ccx_namesuffix ? clientData.ccx_namesuffix : null,
      },
      ccx_alias1: {
        type: "string",
        value:
          clientData.ccx_alias1 !== undefined ? clientData.ccx_alias1 : null,
      },
      ccx_alias2: {
        type: "string",
        value:
          clientData.ccx_alias2 !== undefined ? clientData.ccx_alias2 : null,
      },
      gendercode: {
        type: "optionset",
        value: clientData.gendercode ? clientData.gendercode : null,
      },
      sor_preferredgender: {
        type: "optionset",
        value: clientData.sor_preferredgender
          ? clientData.sor_preferredgender
          : null,
      },
      sor_pronouns: {
        type: "optionset",
        value: clientData.sor_pronouns ? clientData.sor_pronouns : null,
      },
      sor_pronounsother: {
        type: "string",
        value:
          clientData.sor_pronounsother !== undefined
            ? clientData.sor_pronounsother
            : null,
      },
      birthdate: {
        type: "datetime",
        value:
          clientData.birthdate !== undefined
            ? new Date(clientData.birthdate)
            : null,
      },
      ccx_ssn: {
        type: "string",
        value: clientData.ccx_ssn !== undefined ? clientData.ccx_ssn : null,
      },
      telephone2: {
        type: "string",
        value:
          clientData.telephone2 !== undefined ? clientData.telephone2 : null,
      },
      mobilephone: {
        type: "string",
        value:
          clientData.mobilephone !== undefined ? clientData.mobilephone : null,
      },
      telephone1: {
        type: "string",
        value:
          clientData.telephone1 !== undefined ? clientData.telephone1 : null,
      },
      fax: {
        type: "string",
        value: clientData.fax !== undefined ? clientData.fax : null,
      },
      emailaddress1: {
        type: "string",
        value:
          clientData.emailaddress1 !== undefined
            ? clientData.emailaddress1
            : null,
      },
      emailaddress2: {
        type: "string",
        value:
          clientData.emailaddress2 !== undefined
            ? clientData.emailaddress2
            : null,
      },
      ccx_primarycontactmethod: {
        type: "optionset",
        value: clientData.ccx_primarycontactmethod
          ? clientData.ccx_primarycontactmethod
          : null,
      },
      ccx_secondarycontactmethod: {
        type: "optionset",
        value: clientData.ccx_secondarycontactmethod
          ? clientData.ccx_secondarycontactmethod
          : null,
      },
      sor_text: {
        type: "optionset",
        value: clientData.sor_text ? clientData.sor_text : null,
      },
      sor_consideryourselfhispaniclatino: {
        type: "bool",
        value: clientData.sor_consideryourselfhispaniclatino,
      },
      sor_americanindianoralaskanative: {
        type: "bool",
        value: clientData.sor_americanindianoralaskanative,
      },
      sor_nativehawaiianorotherpacificislander: {
        type: "bool",
        value: clientData.sor_nativehawaiianorotherpacificislander,
      },
      sor_blackorafricanamerican: {
        type: "bool",
        value: clientData.sor_blackorafricanamerican,
      },
      sor_white: {
        type: "bool",
        value: clientData.sor_white,
      },
      sor_asian: {
        type: "bool",
        value: clientData.sor_asian,
      },
      sor_mena: {
        type: "bool",
        value: clientData.sor_mena,
      },
      sor_primarylanguage: {
        type: "optionset",
        value: clientData.sor_primarylanguage
          ? clientData.sor_primarylanguage
          : null,
      },
      sor_sexualorientation: {
        type: "optionset",
        value: clientData.sor_sexualorientation
          ? clientData.sor_sexualorientation
          : null,
      },
      sor_pregnantorparentingteen: {
        type: "bool",
        value: clientData.sor_pregnantorparentingteen,
      },
      sor_suiciderisk: {
        type: "optionset",
        value: clientData.sor_suiciderisk ? clientData.sor_suiciderisk : null,
      },
      sor_safetyplanstatus: {
        type: "optionset",
        value: clientData.sor_safetyplanstatus
          ? clientData.sor_safetyplanstatus
          : null,
      },
      parentcustomerid: {
        type: "entity",
        value: {
          entityName: "account",
          entityId: clientData.parentcustomerid
            ? clientData.parentcustomerid
            : null,
        },
      },
      sor_clientflags: {
        type: "string",
        value: clientData.sor_clientflags.join(","),
      },
    };

    if (clientData.contactid !== null) {
      const toastId = toast.loading("Updating Contact...", {
        type: toast.TYPE.INFO,
      });
      updateEntity(
        "contact",
        clientData.contactid,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          toast.update(toastId, {
            render: "Contact has been updated.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
          if (postSave) {
            postSave(clientData);
          }

          if (location?.pathname?.split("/")[1] === "contact") {
            navigate(-1);
          }
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while updating the Contact.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    } else {
      // #region abandoned code - we shouldn't get here
      // const toastId = toast.loading("Creating Contact...", {
      //   type: toast.TYPE.INFO,
      // });
      // createEntity("contact", JSON.stringify(obj), safyInfo) // TODO: how do we want to assign the new Contact? Ask the ccplive user what Business Unit the Contact belongs in?
      //   .then(() => {
      //     toast.update(toastId, {
      //       render: "Contact has been created.",
      //       type: toast.TYPE.SUCCESS,
      //       isLoading: false,
      //       autoClose: 5000,
      //       closeOnClick: true,
      //     });
      //   })
      //   .catch(() => {
      //     toast.update(toastId, {
      //       render: "An error occurred while creating the Contact.",
      //       type: toast.TYPE.ERROR,
      //       isLoading: false,
      //       autoClose: 5000,
      //       closeOnClick: true,
      //     });
      //   });
      // #endregion
    }
  };

  const validateProperty = ({ name, value }) => {
    if (name !== "ccx_ssn") {
      const obj = { [name]: value };
      const schema = { [name]: joiSchema[name] };
      const { error } = Joi.validate(obj, schema, { convert: true });
      return error ? error.details[0].message : null;
    } else return null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(clientData, joiSchema, options);
    if (!error) return null;

    let validationErrors = {};
    for (let item of error.details)
      validationErrors[item.path[0]] = item.message;
    setErrors(validationErrors);
    return validationErrors;
  };

  return (
    <>
      <div>
        <BackIcon />
        {!loaded ? (
          <>
            <div className="d-flex justify-content-center align-items-center">
              <h1>LOADING CONTACT INFO</h1>
              <Lottie
                animationData={contactLoading.default}
                loop={true}
                autoplay={true}
                rendererSettings={{
                  preserveAspectRatio: "xMidYMid slice",
                }}
                height={120}
                width={120}
              />
            </div>
          </>
        ) : (
          <div className="container">
            <div className="row">
              <div className="col-md-3">
                <Select
                  name="ccx_contacttype"
                  label={<h4>Type</h4>}
                  options={
                    contactOptionsets["ccx_contacttype"] &&
                    contactOptionsets["ccx_contacttype"]
                  }
                  value={clientData.ccx_contacttype}
                  onChange={handleChange}
                  error={errors.ccx_contacttype}
                  disabled={clientData.ccx_contacttype === "803080001"}
                />
                {clientData.ccx_contacttype === "229580006" && (
                  <Select
                    name="parentcustomerid"
                    label="County"
                    value={clientData.parentcustomerid}
                    options={countyOptions}
                    onChange={handleChange}
                    error={errors.parentcustomerid}
                  />
                )}
              </div>
              {safyInfo?.user?.modules.includes("Admin") ||
              safyInfo?.user?.modules.includes("Rev Cycle") ? (
                <div className="col-md-3">
                  <h4>Billing Only</h4>
                  <FormCheckbox
                    name="billing"
                    checked={clientData.sor_clientflags.includes("billing")}
                    onChange={handleClientFlagsChange}
                    divClassName="ms-3"
                  />
                </div>
              ) : null}
              <div className="col-md-3">
                <Input
                  name="ccx_mpistr"
                  label={<h4>MPI</h4>}
                  value={clientData.ccx_mpistr}
                  disabled={true}
                  error={errors.ccx_mpistr}
                />
              </div>
              <div className="col-md-3">
                {clientData.ccx_contacttype === "803080001" ? (
                  <Select
                    name="sfy_referringagency"
                    label={<h4>Referring Agency</h4>}
                    value={clientData.sfy_referringagency}
                    options={countyOptions}
                    onChange={handleChange}
                    error={errors.sfy_referringagency}
                  />
                ) : null}
              </div>
            </div>
            <div className="row row-cols-1 row-cols-sm-1 row-cols-md-1 row-cols-lg-2 row-cols-xl-2">
              <div className="col pt-4">
                <div className="h4">Details</div>
                <div className="row row-cols-1 row-cols-sm-1 row-cols-md-3 row-cols-lg-3 row-cols-xl-3">
                  <div className="col p-2">
                    <Input
                      name="firstname"
                      label="First Name"
                      value={clientData.firstname}
                      onChange={handleChange}
                      error={errors.firstname}
                    />
                  </div>
                  <div className="col p-2">
                    <Input
                      name="middlename"
                      label="Middle Name"
                      value={clientData.middlename}
                      onChange={handleChange}
                      error={errors.middlename}
                    />
                  </div>
                  <div className="col p-2">
                    <Input
                      name="lastname"
                      label="Last Name"
                      value={clientData.lastname}
                      onChange={handleChange}
                      error={errors.lastname}
                    />
                  </div>
                </div>
                <div className="row row-cols-1 row-cols-sm-1 row-cols-md-3 row-cols-lg-3 row-cols-xl-3">
                  <div className="col">
                    <Input
                      name="ccx_alias1"
                      label="Preferred/Alias First Name"
                      value={clientData.ccx_alias1}
                      onChange={handleChange}
                      error={errors.ccx_alias1}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="ccx_alias2"
                      label="Preferred/Alias Last Name"
                      value={clientData.ccx_alias2}
                      onChange={handleChange}
                      error={errors.ccx_alias2}
                    />
                  </div>
                  <div className="col">
                    <Select
                      name="ccx_namesuffix"
                      label="Suffix"
                      options={
                        contactOptionsets["ccx_namesuffix"] &&
                        contactOptionsets["ccx_namesuffix"]
                      }
                      value={clientData.ccx_namesuffix}
                      onChange={handleChange}
                      error={errors.ccx_namesuffix}
                    />
                  </div>
                </div>
                <div className="row row-cols-1 row-cols-sm-1 row-cols-md-2 row-cols-lg-2 row-cols-xl-2">
                  <div className="col">
                    <Input
                      name="birthdate"
                      type="date"
                      label="Birth Date"
                      value={
                        clientData.birthdate && formatDate(clientData.birthdate)
                      }
                      onChange={handleChange}
                      error={errors.birthdate}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="ccx_ssn"
                      label="SSN"
                      value={clientData.ccx_ssn && clientData.ccx_ssn}
                      onChange={handleChange}
                      error={errors.ccx_ssn}
                    />
                  </div>
                </div>
                <div className="row row-cols-1 row-cols-sm-1 row-cols-md-3 row-cols-lg-3 row-cols-xl-3">
                  <div className="col">
                    <Select
                      name="gendercode"
                      label="Sex"
                      options={
                        contactOptionsets["gendercode"] &&
                        contactOptionsets["gendercode"]
                      }
                      value={clientData.gendercode}
                      onChange={handleChange}
                      error={errors.gendercode}
                    />
                  </div>
                  <div className="col">
                    <Select
                      name="sor_preferredgender"
                      label="Gender"
                      options={
                        contactOptionsets["sor_preferredgender"] &&
                        contactOptionsets["sor_preferredgender"]
                      }
                      value={clientData.sor_preferredgender}
                      onChange={handleChange}
                      error={errors.sor_preferredgender}
                    />
                  </div>
                  <div className="col">
                    <Select
                      name="sor_sexualorientation"
                      label="Sexual Orientation"
                      options={
                        contactOptionsets["sor_sexualorientation"] &&
                        contactOptionsets["sor_sexualorientation"]
                      }
                      value={clientData.sor_sexualorientation}
                      onChange={handleChange}
                      error={errors.sor_sexualorientation}
                    />
                  </div>
                </div>
                <div className="row row-cols-1 row-cols-sm-1 row-cols-md-3 row-cols-lg-3 row-cols-xl-3">
                  <div className="col">
                    <Select
                      name="sor_pronouns"
                      label="Pronouns"
                      options={
                        contactOptionsets["sor_pronouns"] &&
                        contactOptionsets["sor_pronouns"]
                      }
                      value={clientData.sor_pronouns}
                      onChange={handleChange}
                      error={errors.sor_pronouns}
                    />
                  </div>
                  <div className="col">
                    {clientData.sor_pronouns === "186460003" ? (
                      <Input
                        name="sor_pronounsother"
                        label="Pronouns description"
                        value={clientData.sor_pronounsother}
                        onChange={handleChange}
                        error={errors.sor_pronounsother}
                      />
                    ) : null}
                  </div>
                  <div className="col"></div>
                </div>
                {clientData.ccx_contacttype === "803080001" && (
                  <div className="row mt-5">
                    <div className="col">
                      <Select
                        name="sor_suiciderisk"
                        label="Suicide Risk"
                        options={
                          contactOptionsets["sor_suiciderisk"] &&
                          contactOptionsets["sor_suiciderisk"]
                        }
                        value={clientData.sor_suiciderisk}
                        onChange={handleChange}
                        error={errors.sor_suiciderisk}
                      />
                    </div>
                    <div className="col">
                      <Select
                        name="sor_safetyplanstatus"
                        label="Safety Plan Status"
                        options={
                          contactOptionsets["sor_safetyplanstatus"] &&
                          contactOptionsets["sor_safetyplanstatus"]
                        }
                        value={clientData.sor_safetyplanstatus}
                        onChange={handleChange}
                        error={errors.sor_safetyplanstatus}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="medFrag"
                        label="Medically Fragile"
                        checked={clientData.sor_clientflags.includes("medFrag")}
                        onChange={handleClientFlagsChange}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="chartHolder"
                        label="Family Chart Holder"
                        checked={clientData.sor_clientflags.includes(
                          "chartHolder"
                        )}
                        onChange={handleClientFlagsChange}
                        divClassName="ms-3"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="col pt-4 ps-4">
                <div className="h4 mb-4">Demographics</div>
                <div className="row">
                  <div className="col ps-4">
                    <Checkbox
                      name="sor_consideryourselfhispaniclatino"
                      label="Considered Hispanic or Latino"
                      checked={
                        clientData.sor_consideryourselfhispaniclatino === true
                      }
                      onChange={handleChecked}
                      error={errors.sor_consideryourselfhispaniclatino}
                    />
                    <Checkbox
                      name="sor_americanindianoralaskanative"
                      label="American Indian or Alaskan Native"
                      checked={clientData.sor_americanindianoralaskanative}
                      onChange={handleChecked}
                      error={errors.sor_americanindianoralaskanative}
                    />
                    <Checkbox
                      name="sor_nativehawaiianorotherpacificislander"
                      label="Native Hawaiian or Pacific Islander"
                      checked={
                        clientData.sor_nativehawaiianorotherpacificislander
                      }
                      onChange={handleChecked}
                      error={errors.sor_nativehawaiianorotherpacificislander}
                    />
                    <Checkbox
                      name="sor_blackorafricanamerican"
                      label="Black or African American"
                      checked={clientData.sor_blackorafricanamerican}
                      onChange={handleChecked}
                      error={errors.sor_blackorafricanamerican}
                    />
                    <Checkbox
                      name="sor_white"
                      label="White"
                      checked={clientData.sor_white}
                      onChange={handleChecked}
                      error={errors.sor_white}
                    />
                    <Checkbox
                      name="sor_asian"
                      label="Asian"
                      checked={clientData.sor_asian}
                      onChange={handleChecked}
                      error={errors.sor_asian}
                    />
                    <Checkbox
                      name="sor_mena"
                      label={
                        <div className="d-flex">
                          Middle Eastern or Northern African
                          <div
                            data-tooltip-id="definition-tooltip"
                            data-tooltip-html={ReactDOMServer.renderToString(
                              MENATooltip
                            )}
                          >
                            <i className="fas fa-info-circle ms-2 text-info"></i>
                          </div>
                        </div>
                      }
                      checked={clientData.sor_mena}
                      onChange={handleChecked}
                      error={errors.sor_mena}
                    />
                    <Select
                      name="sor_primarylanguage"
                      label="Primary Language"
                      options={
                        contactOptionsets["sor_primarylanguage"] &&
                        contactOptionsets["sor_primarylanguage"]
                      }
                      value={clientData.sor_primarylanguage}
                      onChange={handleChange}
                      error={errors.sor_primarylanguage}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="h4">Contact Methods</div>
            <div className="row">
              <div className="col-md-3">
                <PhoneInput
                  name="telephone2"
                  label="Home Phone"
                  value={clientData.telephone2 && clientData.telephone2}
                  onChange={handleChange}
                  error={errors.telephone2}
                />
              </div>
              <div className="col-md-3">
                <PhoneInput
                  name="mobilephone"
                  label="Mobile Phone"
                  value={clientData.mobilephone && clientData.mobilephone}
                  onChange={handleChange}
                  error={errors.mobilephone}
                />
              </div>
              <div className="col-md-3">
                <PhoneInput
                  name="telephone1"
                  label="Business Phone"
                  value={clientData.telephone1 && clientData.telephone1}
                  onChange={handleChange}
                  error={errors.telephone1}
                />
              </div>
              <div className="col-md-3">
                <PhoneInput
                  name="fax"
                  label="Fax"
                  value={clientData.fax && clientData.fax}
                  onChange={handleChange}
                  error={errors.fax}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-4">
                <Select
                  name="sor_text"
                  label="Can Text?"
                  options={
                    contactOptionsets["sor_text"] &&
                    contactOptionsets["sor_text"]
                  }
                  value={clientData.sor_text && clientData.sor_text}
                  onChange={handleChange}
                  error={errors.sor_text}
                />
              </div>
              <div className="col-md-4">
                <Input
                  name="emailaddress1"
                  label="Email 1"
                  value={clientData.emailaddress1 && clientData.emailaddress1}
                  onChange={handleChange}
                  error={errors.emailaddress1}
                />
              </div>
              <div className="col-md-4">
                <Input
                  name="emailaddress2"
                  label="Email 2"
                  value={clientData.emailaddress2 && clientData.emailaddress2}
                  onChange={handleChange}
                  error={errors.emailaddress2}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <Select
                  name="ccx_primarycontactmethod"
                  label="Primary Contact Method"
                  options={
                    contactOptionsets["ccx_primarycontactmethod"] &&
                    contactOptionsets["ccx_primarycontactmethod"]
                  }
                  value={clientData.ccx_primarycontactmethod}
                  onChange={handleChange}
                  error={errors.ccx_primarycontactmethod}
                />
              </div>
              <div className="col-md-6">
                <Select
                  name="ccx_secondarycontactmethod"
                  label="Secondary Contact Method"
                  options={
                    contactOptionsets["ccx_secondarycontactmethod"] &&
                    contactOptionsets["ccx_secondarycontactmethod"]
                  }
                  value={clientData.ccx_secondarycontactmethod}
                  onChange={handleChange}
                  error={errors.ccx_secondarycontactmethod}
                />
              </div>
            </div>
            <AddressesEntity
              clientId={clientId}
              addresses={addresses}
              showExpired={false}
              updateAddresses={handleUpdateAddresses}
              businessUnitName={location?.state?.businessUnitName}
            ></AddressesEntity>
            <div className="row">
              <div className="col">
                {location?.pathname?.split("/")[1] === "contact" && (
                  <>
                    <button
                      className="btn btn-danger m-2"
                      onClick={() => {
                        cancel();
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className={
                        Object.keys(errors).length === 0
                          ? "btn btn-primary m-2"
                          : "btn btn-secondary m-2"
                      }
                      disabled={Object.keys(errors).length !== 0}
                      onClick={() => {
                        if (!validate()) handleSave();
                      }}
                    >
                      Save &amp; Continue
                    </button>
                    <button
                      className={
                        Object.keys(errors).length === 0
                          ? "btn btn-primary m-2"
                          : "btn btn-secondary m-2"
                      }
                      disabled={Object.keys(errors).length !== 0}
                      onClick={() => {
                        if (!validate()) {
                          handleSave();
                        }
                      }}
                    >
                      Save &amp; Close
                    </button>
                  </>
                )}
                {location?.pathname?.split("/")[1] !== "contact" && (
                  <button
                    className={
                      Object.keys(errors).length === 0
                        ? "btn btn-primary m-2"
                        : "btn btn-secondary m-2"
                    }
                    disabled={Object.keys(errors).length !== 0}
                    onClick={() => {
                      if (!validate()) handleSave();
                    }}
                  >
                    Save Contact
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <Tooltip
        className="border-dark text-black fw-bold"
        id="definition-tooltip"
      ></Tooltip>
    </>
  );
};

export default Contact;
